import { useEffect, useState, ReactNode } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import SwiperCore from 'swiper/core';
import { v4 as uuidv4 } from 'uuid';

import { IProductTemplate } from 'shared/types/ProductTemplate';
import { IProductSearch } from 'shared/types/ProductSearch';
import { useLogInBeforeCheckout } from 'productTemplate/hooks/useLogInBeforeCheckout';
import { variableFontRegular } from 'styles/theme/text';
import { GlobalPricingInfo } from 'productTemplate/components/buyBar/GlobalPricingInfo';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { sendBuyBarTrackingEvent } from 'productTemplate/utils/sendBuyBarTrackingEvent';
import { logAddToCart } from 'tracking/utils/eCommerceEvents';
import { fireSnapchatAddCart } from 'tracking/utils/snapchatEvents';
import { FeatureFlags } from 'featureFlags/constants';
import { useCurrency } from 'shared/hooks/useCurrency';
import { fireTiktokCheckoutAddCart } from 'tracking/utils/tiktokEvents';
import { IVariantData } from 'productTemplate/components/buyBar/VariantDetail';
import { IProductImage } from 'shared/types/ProductImage';
import { IProduct } from 'shared/types/Product';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { removeWindow, setMobileView } from 'store/slice/pdpStateSlice';
import { t } from 'localization/utils/t';
import { startCase } from 'shared/utils/startCase';
import { useGenderLabel } from 'shared/hooks/useGenderLabel';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'productTemplate.hooks.useVariantDetailData';

const PlainText = styled.div`
  ${variableFontRegular({ height: '12px', size: '9.5px' })}
`;

const LearnMore = styled.span`
  text-transform: capitalize;
  text-decoration: underline;
`;

const GlobalPricingWrapper = styled(PlainText)`
  ${GlobalPricingInfo.Text} {
    grid-column-start: 2;
    text-align: left;
    margin: 0;
    white-space: normal;
  }
`;

interface IDetail {
  label: string;
  value: string;
}

export interface IVariantDetailData {
  productTemplateId: number;
  name: string;
  year: string | number;
  price: number;
  variantId: string;
  canReturn: boolean;
  carousel: {
    carouselImages: IProductImage[];
    nextClassName: string;
    previousClassName: string;
    progressIndicatorCurrent: number;
    progressIndicatorTotal: number;
    handleSlideChange: (swipercore: SwiperCore) => void;
  };
  size: number;
  slug: string;
  shoeCondition: string;
  boxCondition: string;
  details: IDetail[];
  termsCopy: ReactNode;
  globalPricing: ReactNode;
  checkoutLink: string;
  handleCheckoutClick: (e) => void;
  handleViewMoreClick: () => void;
}

export const useVariantDetailData = (variantData: IVariantData): IVariantDetailData => {
  const storeDispatch = useStoreDispatch();
  const getGenderLabel = useGenderLabel();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const { productTemplate, buyBarVariants } = useStoreSelector((state) => state.pdp);
  const {
    name,
    slug,
    productType,
    sizeOptions,
    brandName,
    releaseDate,
    releaseYear,
    singleGender,
    sizeUnit,
    sku,
    specialType,
    isFashionProduct,
    id: productTemplateId,
  }: IProductTemplate = productTemplate;
  const { availableSizes } = buyBarVariants;
  const { currentSearchTerm } = useStoreSelector((state) => state.search);
  const { logInBeforeCheckout } = useLogInBeforeCheckout();
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);
  const ConstructorClient = useConstructorClient();
  const isApparel = productType !== 'sneakers';
  const year = releaseYear || new Date(releaseDate).getFullYear() || '';
  const isSnapchatEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_SNAPCHAT);
  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const isBoxConditionEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_NON_SNEAKER_BOX_CONDITION,
  );

  const { selectedCurrencyCode } = useCurrency();

  const handleSlideChange = (swipercore) => {
    const { activeIndex: idx } = swipercore;
    setActiveIndex(idx);
  };

  const {
    id,
    productImages = [],
    localizedPriceCents: { amount: price },
    localizedLowestPriceCents,
    localizedPriceCents,
    boxCondition,
    shoeCondition,
    defectsString,
    size,
    listingDescription,
  } = variantData;

  const { canReturn = false } = variantData as IProduct;

  const { isGoatClean } = variantData as IProductSearch;

  const checkoutBaseUrl = '/checkout?';
  const params = new URLSearchParams({
    expectedPriceCents: (localizedLowestPriceCents?.amountUsdCents || price).toString(),
    size: String(size),
    shoeCondition,
    productId: id.toString(),
    productTemplateId: productTemplateId.toString(),
  });

  const checkoutLink = getRedirectionPath(`${checkoutBaseUrl}${params.toString()}`);

  useEffect(() => {
    sendTrackingEvent('MODAL_OPEN_VIEW', {
      product_template_slug: slug,
      modal_name: 'variant_detail',
    });
  }, []);

  useEffect(() => {
    sendTrackingEvent('PRODUCT_VIEW', {
      currency_code: localizedPriceCents.currency,
      condition_category: shoeCondition,
      shoe_condition: shoeCondition,
      box_condition: boxCondition,
      size,
      price: localizedPriceCents.amount,
      product_template_slug: slug,
      product_id: id,
    });
  }, []);

  const handleCheckoutClick = (e) => {
    if (!isAuthenticated && !isGuestCheckoutEnabled) {
      e.preventDefault();
      logInBeforeCheckout(checkoutLink);
    }

    const clientDedupID = uuidv4();

    sendBuyBarTrackingEvent({
      eventName: 'CART_ADD',
      productVariants: availableSizes,
      retailPrice: price,
      slug,
      section: '',
      sku,
      size,
      isApparelProduct: isFashionProduct,
      clientDedupID,
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });

    logAddToCart({
      priceCents: price,
      condition: shoeCondition,
      name,
      brandName,
      sku,
    });

    if (isSnapchatEnabled) {
      void fireSnapchatAddCart({
        brandName,
        productPrice: price,
        sku,
        clientDedupID,
      });
    }

    if (isTiktokEnabled) {
      void fireTiktokCheckoutAddCart(
        {
          contentCategory: brandName,
          contentId: sku,
          currency: selectedCurrencyCode,
          price,
          quantity: 1,
        },
        user?.email,
      );
    }

    ConstructorClient?.tracker?.trackConversion(currentSearchTerm, {
      item_id: id.toString(),
      revenue: price,
    });
  };

  const handleViewMoreClick = () => {
    storeDispatch(removeWindow(variantData));
    storeDispatch(setMobileView({ view: 'expanded', size }));
  };

  const carouselImages = productImages.filter(
    (img, idx, arr) =>
      (!img.url.includes('missing.png') && img.pictureType !== 'ShoeTemplate' && arr.length > 1) ||
      (arr.length <= 1 && img.pictureType === 'ShoeTemplate'),
  );

  const getApparelSizeDisplay = () => {
    const sizeDisplay = sizeOptions.find(({ value }) => value === size);
    return sizeDisplay?.presentation?.toUpperCase();
  };

  const sneakerSizeDisplay = t(`${TP}.sizeGender`, '{sizeUnit} {gender} {size}', {
    sizeUnit: sizeUnit.toUpperCase(),
    gender: startCase(getGenderLabel(singleGender), { capitalizeInOtherLanguages: false }),
    size,
  });

  let details: IDetail[];

  if (isApparel) {
    details = [
      { label: t(`${TP}.size`, 'Size'), value: getApparelSizeDisplay() },
      {
        label: t(`${TP}.condition`, 'Condition'),
        value: specialType === 'vintage' ? t(`${TP}.vintage`, 'Vintage') : t(`${TP}.used`, 'Used'),
      },
    ];

    if (isBoxConditionEnabled && boxCondition) {
      details.push({
        label: t(`${TP}.box`, 'Box'),
        value: startCase(boxCondition),
      });
    }
    if (listingDescription) {
      details.push({
        label: 'listingDescription',
        value: listingDescription,
      });
    }
  } else {
    details = [
      {
        label: t(`${TP}.size`, 'Size'),
        value: sneakerSizeDisplay,
      },
      {
        label: t(`${TP}.condition`, 'Condition'),
        value: isGoatClean ? t(`${TP}.goatClean`, 'GOAT Clean') : startCase(shoeCondition),
      },
      {
        label: t(`${TP}.box`, 'Box'),
        value: startCase(boxCondition),
      },
    ];
    if (defectsString) {
      details.push({
        label: t(`${TP}.notes`, 'Notes'),
        value: startCase(defectsString).replace(/,|, /g, ', '),
      });
    }
  }

  const termsCopy = (
    <PlainText data-qa="terms_copy">
      {shoeCondition === 'used'
        ? t(`${TP}.finalSaleUsed`, 'Purchases of used items are final sale')
        : t(`${TP}.finalSale`, 'This item is final sale')}{' '}
      <LearnMore>
        <Link href="/returns" passHref target="_blank" rel="noopener noreferrer">
          {t(`${TP}.learnMore`, 'learn more')}
        </Link>
      </LearnMore>
    </PlainText>
  );

  const globalPricing = (
    <GlobalPricingWrapper>
      <GlobalPricingInfo location="used_pdp" />
    </GlobalPricingWrapper>
  );

  return {
    productTemplateId,
    name,
    year,
    price,
    canReturn,
    variantId: String(id),
    carousel: {
      carouselImages,
      nextClassName: `next-${String(id)}`,
      previousClassName: `prev-${String(id)}`,
      progressIndicatorCurrent: activeIndex + 1,
      progressIndicatorTotal: carouselImages.length,
      handleSlideChange,
    },
    slug,
    size,
    shoeCondition,
    boxCondition,
    details,
    termsCopy,
    globalPricing,
    checkoutLink,
    handleCheckoutClick,
    handleViewMoreClick,
  };
};
